import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { X } from 'lucide-react';

import { cn } from '../../utils/cn';

import {
    ToastActionButton,
    type ToastNotificationAction,
} from './toast-action-button';

export type { ToastNotificationAction } from './toast-action-button';

export type ToastNotificationProps = {
    /** Controlled open state. */
    open: boolean;
    /** Called when the toast requests open/close (close control or caller). */
    onOpenChange: (open: boolean) => void;
    title: React.ReactNode;
    description?: React.ReactNode;
    actions?: ToastNotificationAction[];
    /** Accessible label for the close control. */
    closeLabel?: string;
    className?: string;
    /**
     * When this value changes while the toast stays open, the interior content
     * (title, description, actions) remounts with a short fade-in and soft height
     * transition, without replaying the shell enter animation. Omit when content
     * never swaps in place — motion measurement stays off in that case.
     */
    contentKey?: string | number;
};

type ToastBodyProps = {
    title: React.ReactNode;
    description?: React.ReactNode;
    actions: ToastNotificationAction[];
};

/** Title, description, and action row shared by flat and content-swap layouts. */
function ToastBody({ title, description, actions }: ToastBodyProps) {
    return (
        <>
            <div className="flex flex-col gap-1.5 pr-2">
                <ToastPrimitives.Title className="m-0 text-xl leading-none font-medium text-[#f1f1f1]">
                    {title}
                </ToastPrimitives.Title>
                {description != null ? (
                    <ToastPrimitives.Description className="m-0 text-lg leading-[1.2] font-normal text-[#f1f1f1]">
                        {description}
                    </ToastPrimitives.Description>
                ) : null}
            </div>

            {actions.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2.5">
                    {actions.map((action) => (
                        <ToastActionButton key={action.id} action={action} />
                    ))}
                </div>
            ) : null}
        </>
    );
}

type SoftHeightContentProps = ToastBodyProps & {
    contentKey: string | number;
};

/**
 * Measured-height shell + keyed fade-in for ask ↔ thank-you style content swaps.
 * Only mounted when the caller passes `contentKey`.
 */
function SoftHeightContent({
    contentKey,
    title,
    description,
    actions,
}: SoftHeightContentProps) {
    const contentMeasureRef = React.useRef<HTMLDivElement>(null);
    const [contentHeightPx, setContentHeightPx] = React.useState<number | null>(
        null,
    );

    React.useLayoutEffect(() => {
        const measureElement = contentMeasureRef.current;
        if (!measureElement) {
            return;
        }

        const updateHeight = () => {
            setContentHeightPx(measureElement.getBoundingClientRect().height);
        };

        updateHeight();
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(measureElement);
        return () => {
            resizeObserver.disconnect();
        };
        // contentKey remounts the measured node; ResizeObserver covers later size changes.
    }, [contentKey]);

    return (
        <div
            className={cn(
                'overflow-hidden transition-[height] ease-out',
                'duration-[200ms] motion-reduce:transition-none',
            )}
            style={
                contentHeightPx == null
                    ? undefined
                    : { height: contentHeightPx }
            }
            data-testid="toast-notification-height-shell"
        >
            <div
                key={contentKey}
                ref={contentMeasureRef}
                className={cn(
                    'flex flex-col gap-8.5',
                    'animate-in duration-[200ms] fade-in-0 motion-reduce:animate-none',
                )}
                data-testid="toast-notification-content"
                data-content-key={String(contentKey)}
            >
                <ToastBody
                    title={title}
                    description={description}
                    actions={actions}
                />
            </div>
        </div>
    );
}

/**
 * Controlled bottom-right action toast with isolated Radix toast primitives.
 * Presentation only — callers own copy, icons, and action behaviour.
 *
 * Mount one instance and toggle it with `open`. Keep the instance mounted after
 * the first open and drive visibility only via `open` (including `open={false}`)
 * so Radix can run `data-[state=closed]` exit animation. Hold last title /
 * description / actions while closed so exit has content to animate. Unmounting
 * on hide skips exit.
 *
 * Each instance owns its own fixed viewport, so multiple mounted instances will
 * stack competing viewports.
 *
 * Accessibility: Radix announces open toasts via a polite live region, and both
 * Escape and the labelled close control request a close without trapping focus.
 * One Escape press yields exactly one `onOpenChange(false)`.
 */
export function ToastNotification({
    open,
    onOpenChange,
    title,
    description,
    actions = [],
    closeLabel = 'Close',
    className,
    contentKey,
}: ToastNotificationProps) {
    /**
     * Radix sees one Escape press twice when the toast holds focus: once from its
     * document capture listener, then again from the toast's own keydown handler.
     * Remembering the native event lets the second pass cancel itself, so callers
     * that persist a dismissal are never asked to do it twice.
     */
    const handledEscapeEventRef = React.useRef<KeyboardEvent | null>(null);
    const handleEscapeKeyDown = (event: KeyboardEvent) => {
        if (handledEscapeEventRef.current === event) {
            event.preventDefault();
            return;
        }

        handledEscapeEventRef.current = event;
    };

    const bodyProps: ToastBodyProps = { title, description, actions };

    return (
        <ToastPrimitives.Provider duration={Infinity} label="Notification">
            <ToastPrimitives.Root
                open={open}
                onOpenChange={onOpenChange}
                duration={Infinity}
                onEscapeKeyDown={handleEscapeKeyDown}
                className={cn(
                    'pointer-events-auto relative flex w-full max-w-[min(542px,calc(100vw-2rem))] flex-col rounded-[10px] bg-[#1a1a1a] py-5 pr-10 pl-3.75 text-[#f1f1f1] shadow-[0_5px_34px_rgba(0,0,0,0.15)]',
                    contentKey == null && 'gap-8.5',
                    'data-[state=closed]:animate-out data-[state=open]:animate-in',
                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                    'data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4',
                    'data-[state=closed]:duration-[200ms] data-[state=open]:duration-[250ms]',
                    'motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none',
                    'motion-reduce:data-[state=closed]:opacity-0 motion-reduce:data-[state=open]:opacity-100',
                    className,
                )}
            >
                {contentKey != null ? (
                    <SoftHeightContent contentKey={contentKey} {...bodyProps} />
                ) : (
                    <ToastBody {...bodyProps} />
                )}

                <ToastPrimitives.Close
                    type="button"
                    aria-label={closeLabel}
                    className={cn(
                        'absolute top-2.5 right-2.5 inline-flex size-[17px] items-center justify-center rounded-sm text-[#f1f1f1]',
                        'opacity-80 transition-opacity hover:opacity-100',
                        'focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1a1a] focus-visible:outline-hidden',
                    )}
                >
                    <X className="size-4" aria-hidden />
                </ToastPrimitives.Close>
            </ToastPrimitives.Root>

            <ToastPrimitives.Viewport
                className={cn(
                    'fixed right-0 bottom-0 z-[100] flex w-full max-w-[min(542px,calc(100vw-1rem))] flex-col items-end gap-2 p-6 outline-none',
                    'sm:right-8 sm:bottom-8 sm:p-0',
                )}
            />
        </ToastPrimitives.Provider>
    );
}

ToastNotification.displayName = 'ToastNotification';
